import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Highlight } from "@/components/QuestionCard";
import { EXPLORE_LINKS } from "@/lib/explore";
import { useExploreLinks } from "@/hooks/use-site-settings";
import { useCommentaries } from "@/components/commentary/CommentaryPanel";
import { useBlogSettings } from "@/components/blog/BlogBits";
import { Sun, Moon, Bookmark, Menu, X, LogOut, LayoutDashboard, Settings, HelpCircle, ShieldCheck, Search, Loader2, ArrowRight, ChevronDown, NotebookPen, Highlighter } from "lucide-react";

const NAV = [
  { label: "Bible Reader", to: "/read" },
  { label: "Commentary", to: "/commentary" },
  { label: "Daily Spark", to: "/daily" },
  { label: "Blog", to: "/blog" },
  { label: "Why We Ask", to: "/about" },
];

function ExploreMenu({ navLinkCls }) {
  const exploreLinks = useExploreLinks(EXPLORE_LINKS);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const active = location.pathname.startsWith("/explore") || location.pathname.startsWith("/book/") || location.pathname.startsWith("/type/");
  useEffect(() => setOpen(false), [location.pathname]);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link
        to="/explore"
        data-testid="nav-explore"
        aria-haspopup="menu"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors ${navLinkCls} ${active ? "font-semibold" : ""}`}
      >
        Explore <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </Link>
      <div
        role="menu"
        data-testid="explore-menu"
        className={`absolute left-1/2 top-full z-50 w-[22rem] -translate-x-1/2 pt-3 transition-all duration-200 ${open ? "visible translate-y-0 opacity-100" : "invisible -translate-y-1 opacity-0"}`}
      >
        <div className="rounded-2xl border border-border bg-background/95 p-2 shadow-xl glass">
          {exploreLinks.map(({ to, label, blurb, icon: Icon, testId }) => (
            <Link
              key={to}
              to={to}
              role="menuitem"
              data-testid={testId}
              className="group flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-muted"
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold"><Icon className="h-4 w-4" /></span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-foreground">{label}</span>
                <span className="block truncate text-xs text-muted-foreground">{blurb}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const NOTE_LABEL = { purpose: "Why It Was Asked", response: "The Response", historical_context: "Historical Context", commentary: "The Bigger Picture", application: "For Your Life Today" };
const COMMENTARY_NAMES = { "matthew-henry": "Matthew Henry", wiersbe: "Wiersbe", gill: "John Gill", jfb: "JFB", spurgeon: "Spurgeon" };
const GROUP_LABEL = { word: "Word study", question: "Questions", bible: "In the Bible", note: "Study notes", commentary: "Commentaries" };
const GROUP_COUNT = { question: "questions", bible: "bible", note: "notes", commentary: "commentaries" };

function HeaderSearch({ iconCls }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [cursor, setCursor] = useState(-1);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 180);
    return () => clearTimeout(t);
  }, [term]);
  useEffect(() => setCursor(-1), [debounced]);

  const { data, isFetching } = useQuery({
    queryKey: ["live-search", debounced],
    queryFn: async () => (await api.get("/search", { params: { q: debounced, limit: 5 } })).data,
    enabled: open && debounced.length >= 2,
    placeholderData: (prev) => prev,
  });
  const passage = data?.intent === "passage" ? data.reference : null;
  const terms = data?.terms || [];
  const hlq = terms.length ? `?hl=${encodeURIComponent(terms.join(" "))}` : "";
  const questions = debounced.length >= 2 ? (passage ? [...(data.groups?.passage || []), ...(data.chapter_questions || []).filter((q) => !(data.groups?.passage || []).some((e) => e.id === q.id))].slice(0, 5) : data?.groups?.questions || []) : [];
  const notes = passage ? [] : data?.groups?.notes || [];
  const comms = passage ? [] : data?.groups?.commentaries || [];
  const verses = passage ? [] : data?.groups?.bible || [];
  const wordQs = data?.intent === "word" ? data.word?.questions || [] : [];
  const results = debounced.length >= 2 ? [
    ...wordQs.slice(0, 4).map((q) => ({ kind: "word", key: q.id, title: q.question_kjv, meta: [q.reference, `“${q.word}”`], href: `/question/${q.id}` })),
    ...questions.slice(0, 4).map((q) => ({ kind: "question", key: q.id, title: q.question_kjv, meta: [q.reference, q.who_asked?.join(", ")], href: `/question/${q.id}${hlq}` })),
    ...verses.slice(0, 2).map((v) => ({ kind: "bible", key: `b-${v.reference}`, title: v.text, meta: [v.reference, "KJV · open in Bible Reader"], href: v.href })),
    ...notes.slice(0, 2).map((q) => ({ kind: "note", key: `n-${q.id}`, title: q.question_kjv, meta: [q.reference, `found in ${NOTE_LABEL[q.field] || q.field}`], snippet: q.snippet, href: `/question/${q.id}?sec=${q.field}&hl=${encodeURIComponent(terms.join(" "))}` })),
    ...comms.slice(0, 2).map((c) => ({ kind: "commentary", key: `c-${c.commentary}-${c.id}`, title: c.title || c.reference, meta: [COMMENTARY_NAMES[c.commentary] || c.commentary, c.reference], snippet: c.snippet, href: c.href })),
  ] : [];
  const people = data?.groups?.people || [];
  const suggestions = (data?.suggestions || []).filter((sg) => !people.some((p) => p.name === sg.q));
  const counts = data?.counts || {};
  const total = Object.entries(counts).reduce((a, [k, b]) => a + (k === "notes_application" ? 0 : b || 0), 0) + (data?.word?.count || 0);

  const loc = useLocation();
  const go = (path) => {
    setOpen(false);
    setTerm("");
    navigate(path, { state: { from: `${loc.pathname}${loc.search}` } });
  };
  const seeAll = () => debounced && go(`/search?q=${encodeURIComponent(debounced)}`);
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, -1)); }
    else if (e.key === "Enter") { e.preventDefault(); cursor >= 0 && results[cursor] ? go(results[cursor].href) : seeAll(); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="header-search-button"
          aria-label="Search questions"
          className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${iconCls}`}
        >
          <Search className="h-[18px] w-[18px]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className="w-[30rem] max-w-[calc(100vw-2rem)] rounded-2xl p-2" data-testid="header-search-panel">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            autoFocus
            data-testid="header-search-input"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Verse, question, person, word…"
            role="combobox"
            aria-expanded={results.length > 0}
            className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-9 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
          />
          {isFetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
        </div>
        {debounced.length >= 2 && (
          <div className="mt-2 border-t border-border pt-2" data-lenis-prevent>
            {passage && (
              <button type="button" onClick={seeAll} data-testid="header-search-passage" className="mb-1 flex w-full items-center justify-between rounded-xl bg-gold/10 px-3 py-2.5 text-left transition-colors hover:bg-gold/20">
                <span className="text-sm text-foreground"><span className="font-mono text-[10px] uppercase tracking-wider text-gold">Passage</span><br />{passage.invalid ? `${passage.label || passage.book}: ${passage.invalid}` : `Look up ${passage.label}${results.length === 0 ? " — no question recorded here" : ""}`}</span>
                <ArrowRight className="h-4 w-4 shrink-0 text-gold" />
              </button>
            )}
            {suggestions.length > 0 && (
              <div className="mb-1 flex flex-wrap items-center gap-1.5 px-3 py-1.5" data-testid="header-search-suggestions">
                <span className="text-xs text-muted-foreground">{total === 0 ? "Did you mean" : "Try"}</span>
                {suggestions.slice(0, 4).map((sg) => (
                  <button key={sg.q} type="button" onClick={() => setTerm(sg.q)} data-testid={`header-suggestion-${sg.q.replace(/\W+/g, "-")}`} className="rounded-full border border-gold/60 bg-gold/10 px-2.5 py-1 text-xs font-medium text-foreground hover:bg-gold/20">{sg.label}</button>
                ))}
              </div>
            )}
            {results.length === 0 && !isFetching && !passage && total === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground" data-testid="header-search-empty">{suggestions.length ? "No exact matches." : <>Nothing matches &ldquo;{debounced}&rdquo;.</>}</p>
            ) : (
              <ul role="listbox" className="max-h-[60vh] overflow-y-auto">
                {results.map((r, i) => (
                  <li key={r.key} role="option" aria-selected={cursor === i}>
                    {(i === 0 || results[i - 1].kind !== r.kind) && GROUP_LABEL[r.kind] && (
                      <p className="mt-1 px-3 pb-0.5 pt-1.5 font-mono text-[10px] uppercase tracking-wider text-gold" data-testid={`header-group-${r.kind}`}>{GROUP_LABEL[r.kind]}{GROUP_COUNT[r.kind] && counts[GROUP_COUNT[r.kind]] ? ` · ${counts[GROUP_COUNT[r.kind]].toLocaleString()}` : ""}</p>
                    )}
                    <button
                      type="button"
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => go(r.href)}
                      data-testid={`header-search-result-${r.key}`}
                      className={`flex w-full flex-col items-start gap-0.5 rounded-xl px-3 py-2.5 text-left transition-colors ${cursor === i ? "bg-muted" : "hover:bg-muted/60"}`}
                    >
                      <span className={`font-serif text-[15px] leading-snug text-foreground ${r.kind === "bible" ? "line-clamp-2" : ""}`}>
                        <Highlight text={r.title} terms={terms} />
                      </span>
                      <span className="flex flex-wrap items-center gap-x-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                        <span className="text-gold">{r.meta[0]}</span>
                        {r.meta[1] && <span>{r.meta[1]}</span>}
                      </span>
                      {r.snippet && <span className="line-clamp-1 text-xs text-muted-foreground"><Highlight text={r.snippet} terms={terms} /></span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {people.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5 px-3 pb-1" data-testid="header-search-people">
                {people.slice(0, 3).map((p) => (
                  <button key={p.name} type="button" onClick={() => go(`/explore/people/${encodeURIComponent(p.name)}`)} className="rounded-full border border-border px-2.5 py-1 text-xs text-foreground hover:border-gold">{p.name} · {p.count}</button>
                ))}
              </div>
            )}
            {(total > 0 || passage) && (
              <button
                type="button"
                onClick={seeAll}
                data-testid="header-search-see-all"
                className="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-muted/60"
              >
                {passage ? "Open passage lookup" : `See all ${total.toLocaleString()} result${total === 1 ? "" : "s"}`} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        {debounced.length < 2 && (
          <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Type to search · ↑↓ to pick · Enter to open</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function Header() {
  const exploreLinks = useExploreLinks(EXPLORE_LINKS);
  const { theme, toggle } = useTheme();
  const { user, favorites, openAuth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const [scrolled, setScrolled] = useState(false);
  const { data: commentaries } = useCommentaries();
  const { data: blogSettings } = useBlogSettings();
  const showBlog = blogSettings?.enabled || user?.role === "admin";
  const nav = NAV.filter((n) => (n.to !== "/commentary" || !(commentaries && commentaries.length === 0)) && (n.to !== "/blog" || showBlog));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const overHero = location.pathname === "/" && !scrolled;
  const shadow = overHero ? " [text-shadow:_0_1px_10px_rgba(0,0,0,0.75)]" : "";
  const navLinkCls = overHero
    ? "text-white/90 hover:bg-white/10 hover:text-white [text-shadow:_0_1px_8px_rgba(0,0,0,0.75)]"
    : "text-muted-foreground hover:bg-muted hover:text-foreground";
  const iconCls = overHero
    ? "text-white hover:bg-white/10 hover:text-white drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]"
    : "text-muted-foreground hover:bg-muted hover:text-foreground";

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-border bg-background/85 glass shadow-sm"
          : overHero
          ? "border-transparent bg-gradient-to-b from-black/45 via-black/15 to-transparent"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-brand-logo" className="group flex items-center">
          <span className={`font-serif text-2xl font-bold transition-transform duration-200 group-hover:scale-[1.03] ${overHero ? "text-white" : "text-foreground"}${shadow}`}>
            Question<span className={overHero ? "text-[#E8C36A]" : "text-primary"}>God</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <ExploreMenu navLinkCls={navLinkCls} />
          {nav.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${navLinkCls}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <HeaderSearch iconCls={iconCls} />
          <button
            data-testid="theme-toggle-button"
            onClick={toggle}
            aria-label="Toggle theme"
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${iconCls}`}
          >
            {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>

          {user && (
            <Link
              to="/dashboard"
              data-testid="favorites-indicator"
              className={`relative flex h-9 w-9 items-center justify-center rounded-full transition-colors ${iconCls}`}
              aria-label="Saved questions"
            >
              <Bookmark className="h-[18px] w-[18px]" />
              {favorites.length > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground">
                  {favorites.length}
                </span>
              )}
            </Link>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="ml-1 outline-none">
                  <Avatar className="h-9 w-9 border border-border">
                    {user.picture && <AvatarImage src={user.picture} alt={user.name} />}
                    <AvatarFallback className="bg-secondary text-secondary-foreground text-sm font-semibold">
                      {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="truncate text-sm font-semibold text-foreground">{user.name || "Seeker"}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}>
                  <Settings className="mr-2 h-4 w-4" /> Account settings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-journal" onClick={() => navigate("/journal")}>
                  <NotebookPen className="mr-2 h-4 w-4" /> Journal
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-highlights" onClick={() => navigate("/highlights")}>
                  <Highlighter className="mr-2 h-4 w-4" /> Highlights
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-explore" onClick={() => navigate("/explore")}>
                  <HelpCircle className="mr-2 h-4 w-4" /> Explore Questions
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <ShieldCheck className="mr-2 h-4 w-4" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="auth-login-button"
              onClick={openAuth}
              className="hidden rounded-full bg-primary px-5 text-primary-foreground hover:bg-primary/90 sm:inline-flex"
            >
              Sign in
            </Button>
          )}

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className={`flex h-9 w-9 items-center justify-center rounded-full lg:hidden ${iconCls}`}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-border bg-background/95 glass lg:hidden">
          <nav className="flex flex-col px-4 py-3">
            <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Explore by</p>
            {exploreLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-${item.testId}`}
                onClick={closeMobile}
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted"
              >
                <item.icon className="h-4 w-4 text-gold" /> {item.label}
              </Link>
            ))}
            <div className="my-2 h-px bg-border" />
            {nav.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={closeMobile}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted"
              >
                {item.label}
              </Link>
            ))}
            {!user && (
              <Button
                data-testid="mobile-auth-login-button"
                onClick={() => { setMobileOpen(false); openAuth(); }}
                className="mt-2 rounded-full bg-primary text-primary-foreground"
              >
                Sign in
              </Button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
